import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { BrokerOrder, BrokerPosition, useActiveBrokerBalance } from '../store/brokerStore';
import { calculatePnL } from '../config/instruments';
import AutoBeBadge from './AutoBeBadge';
import { RestrainBadge } from './Restrain';
import './LivePositionLine.css';

export interface LivePositionLineProps {
  position: BrokerPosition;
  yPosition: number;
  currentPrice: number;
  onFlatten: () => void;
  symbol?: string; // Fallback symbol (from chart) if position.symbol is undefined
  /** Working orders, for the Auto B/E badge (needs to know TP/SL exist). */
  orders?: BrokerOrder[];
}

export interface LivePositionLineHandle {
  updateY: (y: number) => void;
}

const LivePositionLine = forwardRef<LivePositionLineHandle, LivePositionLineProps>(({
  position,
  yPosition,
  currentPrice,
  onFlatten,
  symbol = '',
  orders = [],
}, ref) => {
  const lineRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    updateY: (y: number) => {
      if (lineRef.current) lineRef.current.style.top = `${y}px`;
      if (controlsRef.current) controlsRef.current.style.top = `${y}px`;
    },
  }));

  const isLong = position.netPos > 0;
  const quantity = Math.abs(position.netPos);
  const entryPrice = position.netPrice;

  const { realizedPnL } = useActiveBrokerBalance();

  // Calculate real-time P&L using current price (same as PositionControlsOverlay)
  // Use position.symbol if available, otherwise fall back to chart symbol
  const pnl = calculatePnL(entryPrice, currentPrice, position.netPos, position.symbol || symbol).dollars;
  const isProfit = pnl >= 0;

  return (
    <>
      {/* Horizontal line extending to the left */}
      <div
        ref={lineRef}
        className={`live-position-line ${isProfit ? 'profit' : 'loss'}`}
        style={{
          top: `${yPosition}px`,
        }}
      />
      {/* Simplified control label - just shows position info */}
      <div
        ref={controlsRef}
        className={`live-position-line-controls ${isLong ? 'long' : 'short'}`}
        style={{
          top: `${yPosition}px`,
          transform: 'translateY(-50%)',
        }}
      >
        <span className="live-position-badge pos">POS</span>
        <span className="live-position-side">{isLong ? 'LONG' : 'SHORT'}</span>
        <span className="live-position-price">{entryPrice.toFixed(2)}</span>
        <span className="live-position-qty">x{quantity}</span>
        <span className={`live-position-pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
          {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}
        </span>
        <span className={`live-position-pnl day-pnl ${(realizedPnL + pnl) >= 0 ? 'positive' : 'negative'}`}>
          D:{(realizedPnL + pnl) >= 0 ? '+' : ''}{(realizedPnL + pnl).toFixed(0)}
        </span>
        <AutoBeBadge position={position} orders={orders} />
        <RestrainBadge position={position} fallbackSymbol={symbol} />
        <button
          className="live-position-flatten-btn"
          onClick={onFlatten}
          title="Flatten position"
        >
          ×
        </button>
      </div>
    </>
  );
});

export default LivePositionLine;
